import { CrosswalkMappingSetType } from 'domains/scope-and-sequence/types'
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Select,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Button,
} from '@mui/material'
import { OutlinedButton } from 'shared/ui'
import React, { useCallback, useState } from 'react'
import { getAlignmentColor } from './PercentStatusBar'
import { useWithErrorNotification } from 'shared/ui/GlobalDialog'
import {
  doAdminQueriesWithResult,
  IQueryInfo,
  IQueryParam,
} from 'infrastructure/api'
import { useSetAtom } from 'infrastructure/state/jotai.wrapper'
import {
  listDataAtom,
  resourceUpdateMap,
} from 'components/resources/resourceList/atoms'
type Alignment = NonNullable<CrosswalkMappingSetType['alignments']>[number]

export function EditCrosswalkModal({
  row,
  setRow,
  crosswalkId,
  onSaved,
}: {
  row: false | Partial<Alignment>
  setRow: (row: false | Partial<Alignment>) => void
  crosswalkId?: number
  onSaved?: () => void
}) {
  const [edits, setEdits] = useState<Partial<Alignment>>({})
  const doSave = useSave(onSaved)

  if (!row || !crosswalkId) {
    return null
  }

  const onClose = () => {
    setEdits({})
    setRow(false)
  }

  async function save() {
    await doSave({ ...row, ...edits, mapping_set_id: crosswalkId })
    onClose()
  }

  const data = { ...row, ...edits }

  return (
    <Dialog open={true} onClose={onClose} fullWidth={true}>
      <DialogTitle>
        {data.id ? `Editing Alignment #${data.id}` : 'Add Alignment'}
      </DialogTitle>
      <form
        method="POST"
        action="#"
        onSubmit={(e) => {
          e.preventDefault()
          void save()
        }}
      >
        <DialogContent>
          <div
            style={{
              paddingTop: '10px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            <TextField
              label={'Source Standard'}
              autoFocus={true}
              value={data.source_standard_code}
              required={true}
              onChange={(e) => {
                setEdits((edits) => ({
                  ...edits,
                  source_standard_code: e.target.value,
                }))
              }}
              helperText={
                data.source_standard_code === row.source_standard_code
                  ? row.source_description
                  : ''
              }
            />
            <TextField
              label={'Target Standard'}
              value={data.target_standard_code}
              required={true}
              onChange={(e) => {
                setEdits((edits) => ({
                  ...edits,
                  target_standard_code: e.target.value,
                }))
              }}
              helperText={
                data.target_standard_code === row.target_standard_code
                  ? row.target_description
                  : ''
              }
            />
            <section style={{ display: 'flex', gap: '16px' }}>
              <FormControl fullWidth>
                <InputLabel id="alignment-type">Alignment Type *</InputLabel>
                <Select
                  labelId="alignment-type"
                  value={data.alignment_type ?? ''}
                  required={true}
                  label="Alignment Type"
                  style={{
                    color: data.alignment_type
                      ? getAlignmentColor(data.alignment_type)
                      : undefined,
                    textTransform: 'capitalize',
                  }}
                  onChange={(e) => {
                    setEdits((edits) => ({
                      ...edits,
                      alignment_type: e.target
                        .value as Alignment['alignment_type'],
                    }))
                  }}
                >
                  {[
                    'full',
                    'related',
                    'partial',
                    'strong partial',
                    'weak partial',
                    'gap',
                  ].map((type) => (
                    <MenuItem
                      key={type}
                      value={type}
                      style={{
                        color: getAlignmentColor(type),
                        textTransform: 'capitalize',
                      }}
                    >
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                type="number"
                id="rating"
                label="Rating (0-100)"
                required={true}
                value={data.rating}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10)
                  if (val >= 0 && val <= 100) {
                    setEdits((edits) => ({
                      ...edits,
                      rating: val,
                    }))
                  }
                }}
              />
            </section>

            <TextField
              label="Note"
              multiline
              minRows={4}
              value={data.note}
              onChange={(e) => {
                setEdits((edits) => ({
                  ...edits,
                  note: e.target.value || null,
                }))
              }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <OutlinedButton onClick={onClose}>Cancel</OutlinedButton>
          <OutlinedButton
            onClick={() => setEdits({})}
            disabled={Object.keys(edits).length === 0}
          >
            Reset Changes
          </OutlinedButton>
          <Button variant="contained" type="submit">
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}

function useSave(onSaved?: () => void) {
  const reFetchHome = useSetAtom(listDataAtom)
  const doSave = useWithErrorNotification(
    { errorTitle: `Error saving Alignment` },
    doAdminQueriesWithResult,
  )

  return useCallback(
    async (editMap: Partial<Alignment>) => {
      const key = editMap.id
        ? resourceUpdateMap.standardAlignments.mutationUpdateKey
        : resourceUpdateMap.standardAlignments.mutationAddKey

      const validKeys = [
        'id',
        'mapping_set_id',
        'source_standard_code',
        'target_standard_code',
        'alignment_type',
        'rating',
        'note',
        'alignment_group_id',
      ]

      const query: IQueryInfo = {
        resourceData: { key },
        params: Object.fromEntries(
          Object.entries(editMap).filter(
            ([key, value]) => validKeys.includes(key) && value !== null,
          ),
        ) as Record<string, IQueryParam>,
      }
      const result = await doSave([query])
      if (result) {
        onSaved?.()
        reFetchHome()
        return true
      }
      return false
    },
    [doSave, onSaved, reFetchHome],
  )
}
